export const FOOTER_LINKS = [
  { key: "about",      label: "About"        },
  { key: "how",        label: "How it works" },
  { key: "privacy",    label: "Privacy"      },
  { key: "terms",      label: "Terms"        },
  { key: "legal",      label: "Legal"        },
  { key: "contact",    label: "Contact"      },
];

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 10 }}>{title}</div>
    {children}
  </div>
);

const P = ({ children }) => (
  <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.8, margin: "0 0 10px" }}>{children}</p>
);

const pages = {
  about: (
    <>
      <Section title="Who we are">
        <P>MioPrice is a healthcare price transparency platform built by MyAtrix LLC. We believe every patient deserves to know the real cost of their care before they receive it — not after.</P>
        <P>We are a technology company, not a healthcare provider. We do not provide medical advice, referrals, or scheduling. We surface publicly available pricing data so patients can make informed decisions.</P>
      </Section>
      <Section title="Our mission">
        <P>The Transparency in Coverage Rule — passed by the federal government — requires every health insurer to publish their negotiated rates for every procedure at every provider. That data exists. But it's buried in machine-readable files that require engineering expertise to parse.</P>
        <P>MioPrice is the translation layer between federal mandate and consumer value. We take that data, make it searchable, and put it in plain language — so patients can finally use the transparency they were promised.</P>
      </Section>
      <Section title="About MyAtrix LLC">
        <P>MyAtrix LLC is an Illinois-based technology company focused on healthcare data transparency. MioPrice is our flagship product.</P>
      </Section>
    </>
  ),

  how: (
    <>
      <Section title="Where the data comes from">
        <P>All pricing data on MioPrice comes from Transparency in Coverage Rule (TCR) machine-readable files — publicly mandated disclosures that every health insurer must publish. These are the actual negotiated rates between insurers and providers, not estimates or averages.</P>
        <P>We currently cover UnitedHealthcare (UHC), Aetna, and Cigna in Illinois. We are expanding to additional payers and states.</P>
      </Section>
      <Section title="What negotiated rates mean">
        <P>When your insurer and a provider sign a contract, they agree on a negotiated rate for each procedure. This is the maximum the provider can charge for that service under that plan. It is legally binding and published under federal law.</P>
        <P>If you have not yet met your deductible, you pay 100% of this negotiated rate. After your deductible is met, you pay your coinsurance percentage. The Cost Check tool calculates exactly what you will owe based on your plan details.</P>
      </Section>
      <Section title="What we do not show">
        <P>MioPrice does not show chargemaster prices (the inflated list price hospitals publish). We do not show estimated costs or averages. We show the actual published negotiated rate — exactly as required by federal law.</P>
      </Section>
    </>
  ),

  privacy: (
    <>
      <Section title="Privacy Policy">
        <P>Effective Date: July 1, 2026</P>
        <P>MioPrice is operated by MyAtrix LLC, an Illinois limited liability company ("Company," "we," "our," or "us"), located in Westmont, Illinois 60559, United States.</P>
        <P>This Privacy Policy describes how we collect, use, and protect your information when you visit mioprice.com.</P>
      </Section>
      <Section title="1. Information We Collect">
        <P>When you visit our website, we may collect:</P>
        <P>• Search queries and ZIP code or city entered to return pricing results</P>
        <P>• IP address, device and browser information</P>
        <P>• Email address (if you create an account or sign in)</P>
        <P>• Cookies and usage data</P>
      </Section>
      <Section title="2. How We Use Your Information">
        <P>• Return relevant healthcare pricing results based on your search</P>
        <P>• Authenticate your account if you sign in</P>
        <P>• Improve our platform experience</P>
        <P>• Prevent fraud and maintain security</P>
      </Section>
      <Section title="3. What We Do Not Collect">
        <P>MioPrice does not collect any personally identifiable health information (PHI) or protected health information. We do not collect your insurance ID, date of birth, medical history, diagnosis, or any health-related information. MioPrice operates entirely on publicly available pricing data published under the federal Transparency in Coverage rule — zero PHI, zero HIPAA risk.</P>
      </Section>
      <Section title="4. Payment Processing">
        <P>MioPrice does not currently process payments directly. Enterprise inquiries are handled via direct contact at sales@mioprice.com.</P>
      </Section>
      <Section title="5. Cookies & Tracking Technologies">
        <P>We may use cookies and standard web analytics to understand website usage and improve platform performance. You may disable cookies in your browser settings.</P>
      </Section>
      <Section title="6. Third-Party Services">
        <P>MioPrice uses Cloudflare for DNS and CDN, Vercel for frontend hosting, Railway for API hosting, and Supabase for authentication. These services may collect standard web traffic data per their own privacy policies. We do not share any user data with data brokers or advertisers.</P>
      </Section>
      <Section title="7. Google Sign-In">
        <P>MioPrice offers the option to sign in using your Google account. When you sign in with Google, we request access to the following information only:</P>
        <P>• Email address — to identify your account</P>
        <P>• Name — to personalize your experience</P>
        <P>We request only the minimum necessary scopes: openid, email, and profile. We do not access, read, or store any other Google account data including Gmail, Google Drive, Google Calendar, or any other Google services. Your Google credentials are never shared with us — authentication is handled securely by Google. You may revoke MioPrice's access at any time by visiting Google Account Permissions.</P>
      </Section>
      <Section title="8. Data Security">
        <P>We implement reasonable technical and organizational safeguards to protect your information. No method of transmission over the Internet is 100% secure.</P>
      </Section>
      <Section title="9. Your Privacy Rights">
        <P>You may request access, correction, or deletion of your personal data by contacting us at privacy@mioprice.com.</P>
      </Section>
      <Section title="10. California Privacy Notice (CCPA)">
        <P>California residents may have additional rights under the CCPA, including the right to request information about personal data collected and to request deletion of such data.</P>
      </Section>
      <Section title="11. Children's Privacy">
        <P>MioPrice is not intended for individuals under 13 years of age.</P>
      </Section>
      <Section title="12. Contact Information">
        <P>MyAtrix LLC (MioPrice)</P>
        <P>Westmont, Illinois 60559</P>
        <P>privacy@mioprice.com</P>
      </Section>
    </>
  ),

  terms: (
    <>
      <Section title="Terms & Conditions">
        <P>Effective Date: July 1, 2026</P>
        <P>These Terms & Conditions govern your use of mioprice.com, operated by MyAtrix LLC, an Illinois limited liability company ("Company," "we," "our," or "us"). By accessing our website, you agree to these Terms.</P>
      </Section>
      <Section title="1. Use of Website">
        <P>You agree to use MioPrice for lawful purposes only. MioPrice is an informational platform providing publicly available healthcare pricing data. It is not a medical advice service, insurance advisor, or healthcare provider.</P>
      </Section>
      <Section title="2. Nature of Pricing Data">
        <P>All pricing data on MioPrice comes from federally mandated Transparency in Coverage Rule machine-readable files published by health insurers. These are real negotiated rates between insurers and providers. However, your actual out-of-pocket costs may vary based on your specific plan design, deductible status, coinsurance, and provider network participation. MioPrice rates are not valid for cash-pay, uninsured, Medicaid, or Medicare patients.</P>
      </Section>
      <Section title="3. No Medical Advice">
        <P>MioPrice does not provide medical advice, diagnosis, treatment recommendations, or referrals. Nothing on this platform should be construed as medical advice. Always consult a qualified healthcare provider for medical decisions.</P>
      </Section>
      <Section title="4. Intellectual Property">
        <P>All content on this website — including text, branding, logos, interface design, and software — is the property of MyAtrix LLC and may not be copied, reproduced, or distributed without written permission. Pricing data is sourced from public federal disclosures and is not proprietary to MyAtrix LLC.</P>
      </Section>
      <Section title="5. Accuracy & Availability">
        <P>We make every effort to display pricing data accurately and keep it current. However, we do not guarantee the completeness, accuracy, or timeliness of any pricing information. Negotiated rates change over time and may differ from what a provider actually bills in a given situation.</P>
      </Section>
      <Section title="6. Limitation of Liability">
        <P>MyAtrix LLC shall not be liable for any healthcare decisions made based on information provided by MioPrice, or for any indirect, incidental, or consequential damages arising from use of this platform. Use of MioPrice is at your own discretion and risk.</P>
      </Section>
      <Section title="7. Indemnification">
        <P>You agree to indemnify and hold harmless MyAtrix LLC from any claims arising from your misuse of MioPrice or violation of these Terms.</P>
      </Section>
      <Section title="8. Governing Law">
        <P>These Terms are governed by the laws of the State of Illinois. Any disputes shall be resolved in Illinois courts.</P>
      </Section>
      <Section title="9. Changes to Terms">
        <P>We reserve the right to update these Terms at any time. Continued use of MioPrice after changes constitutes acceptance of the updated Terms.</P>
      </Section>
      <Section title="10. Contact Information">
        <P>MyAtrix LLC (MioPrice)</P>
        <P>Westmont, Illinois 60559</P>
        <P>privacy@mioprice.com</P>
      </Section>
    </>
  ),

  legal: (
    <>
      <Section title="Data source and compliance">
        <P>All pricing data displayed on MioPrice is sourced from Transparency in Coverage Rule (TCR) machine-readable files, as required under 45 CFR Part 158 and the Consolidated Appropriations Act of 2021. This data is publicly mandated and legally required to be disclosed by health insurers.</P>
        <P>MioPrice displays rates exactly as published in TCR MRF files. We do not aggregate, modify, or misrepresent published negotiated rates. Rates are displayed per payer, per billing class, per service code — as published.</P>
      </Section>
      <Section title="No aggregation of rates">
        <P>MioPrice strictly avoids aggregating minimum and maximum rates across different billing entities, billing classes, or service codes. Each rate displayed corresponds to a specific published row in the source MRF file.</P>
      </Section>
      <Section title="Limitation of liability">
        <P>MyAtrix LLC provides this platform on an "as is" basis. We make no warranties, express or implied, regarding the accuracy, completeness, or fitness for any particular purpose of the pricing information displayed. MyAtrix LLC is not liable for any decisions made based on information provided by MioPrice.</P>
      </Section>
      <Section title="Intellectual property">
        <P>The MioPrice platform, brand, and technology are owned by MyAtrix LLC. Underlying pricing data is publicly mandated under federal law and owned by the respective payers.</P>
      </Section>
    </>
  ),

  contact: (
    <>
      <Section title="Get in touch">
        <P>We'd love to hear from you — whether you have a question about pricing data, want to report an error, or are interested in our B2B products.</P>
      </Section>
      <Section title="General inquiries">
        <P>Email: hello@mioprice.com</P>
      </Section>
      <Section title="Data errors">
        <P>If you believe a rate displayed is incorrect, please email data@mioprice.com with the facility name, procedure, and payer. We will investigate and update as needed.</P>
      </Section>
      <Section title="Business & partnerships">
        <P>For Payer Intelligence or Enrollment Intelligence inquiries, email business@mioprice.com.</P>
      </Section>
      <Section title="Privacy">
        <P>For privacy-related questions, email privacy@mioprice.com.</P>
      </Section>
    </>
  ),
};

const TITLES = {
  about:   "About MioPrice",
  how:     "How It Works",
  privacy: "Privacy Policy",
  terms:   "Terms of Use",
  legal:   "Legal & Compliance",
  contact: "Contact Us",
};

export function PrivacyPageContent() {
  return pages.privacy;
}

export function TermsPageContent() {
  return pages.terms;
}

export default function FooterPage({ page, onClose }) {
  return (
    <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }}>
            <span style={{ cursor: "pointer", color: "#2563eb" }} onClick={onClose}>MioPrice</span> · {TITLES[page]}
          </div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}>{TITLES[page]}</div>
        </div>
        <button onClick={onClose} style={{ background: "#f3f4f6", border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 13, cursor: "pointer", color: "#374151" }}>← Back</button>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "24px 20px" }}>
        {pages[page]}
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 24, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
          Last updated: June 2026 · MyAtrix LLC · mioprice.com
        </div>
      </div>

      {/* Footer nav */}
      <div style={{ borderTop: "1px solid #e5e7eb", padding: "12px 20px", display: "flex", gap: 16, flexWrap: "wrap" }}>
        {FOOTER_LINKS.map(l => (
          <span key={l.key} style={{ fontSize: 12, color: l.key === page ? "#2563eb" : "#6b7280", cursor: "pointer", fontWeight: l.key === page ? 600 : 400 }}>
            {l.label}
          </span>
        ))}
      </div>
    </div>
  );
}
